import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X, Volume2, VolumeX, PackageX, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import api from '../api/client';

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [880, 660];
    notes.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = freq;
      const start = ctx.currentTime + i * 0.22;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.35, start + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      o.start(start); o.stop(start + 0.36);
    });
  } catch (e) { /* ignore */ }
}

// Banner + chime for machines newly in PROBLEM status AND materials at/below reorder (supervisors/admins only).
export default function AlertBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [settingAlerts, setSettingAlerts] = useState([]);
  const [dismissedSet, setDismissedSet] = useState([]);
  const [dismissedMat, setDismissedMat] = useState([]);
  const [muted, setMuted] = useState(() => localStorage.getItem('kmt_alert_muted') === '1');
  const prev = useRef(null);
  const prevLowCodes = useRef(null);
  const prevSetIds = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const canSee = ['ADMIN', 'SUPERVISOR'].includes(user?.role);

  const check = () => {
    api.listMachines().then((data) => {
      const problemIds = new Set(data.filter((m) => m.status === 'PROBLEM').map((m) => m.id));
      if (prev.current === null) { prev.current = problemIds; return; }
      const newly = data.filter((m) => m.status === 'PROBLEM' && !prev.current.has(m.id));
      prev.current = problemIds;
      if (newly.length) {
        setAlerts((a) => {
          const have = new Set(a.map((x) => x.id));
          const add = newly.filter((m) => !have.has(m.id)).map((m) => ({ id: m.id, name: m.displayName }));
          if (add.length && !mutedRef.current) playChime();
          return [...add, ...a].slice(0, 6);
        });
      }
    }).catch((e) => { console.error('AlertBanner machine fetch failed:', e); });

    api.listMaterials().then((mats) => {
      const low = mats.filter((m) => m.status === 'LOW' || m.status === 'CRITICAL');
      setLowStock(low);
      const codes = new Set(low.map((m) => m.code));
      // Re-enable a previously dismissed alert only once the material recovers to OK.
      setDismissedMat((d) => d.filter((c) => codes.has(c)));
      if (prevLowCodes.current === null) { prevLowCodes.current = codes; return; }
      const newlyLow = low.filter((m) => !prevLowCodes.current.has(m.code));
      prevLowCodes.current = codes;
      if (newlyLow.length && !mutedRef.current) playChime();
    }).catch((e) => { console.error('AlertBanner material fetch failed:', e); });

    api.settingAlerts().then((res) => {
      const list = res.alerts || [];
      setSettingAlerts(list);
      const ids = new Set(list.map((a) => a.id));
      setDismissedSet((d) => d.filter((id) => ids.has(id)));
      if (prevSetIds.current === null) { prevSetIds.current = ids; return; }
      const newly = list.filter((a) => !prevSetIds.current.has(a.id));
      prevSetIds.current = ids;
      if (newly.length && !mutedRef.current) playChime();
    }).catch((e) => { console.error('AlertBanner setting fetch failed:', e); });
  };

  useEffect(() => { if (canSee) check(); /* eslint-disable-next-line */ }, [canSee]);
  useRealtime(() => { if (canSee) check(); });

  const toggleMute = () => {
    setMuted((m) => { const n = !m; localStorage.setItem('kmt_alert_muted', n ? '1' : '0'); return n; });
  };

  const visibleLow = lowStock.filter((m) => !dismissedMat.includes(m.code));
  const visibleSet = settingAlerts.filter((a) => !dismissedSet.includes(a.id));
  const totalCount = alerts.length + visibleLow.length + visibleSet.length;

  if (!canSee || totalCount === 0) return null;

  const dismiss = (id) => setAlerts((a) => a.filter((x) => x.id !== id));
  const dismissMat = (code) => setDismissedMat((d) => [...d, code]);
  const dismissSet = (id) => setDismissedSet((d) => [...d, id]);
  const dismissAll = () => { setAlerts([]); setDismissedMat(lowStock.map((m) => m.code)); setDismissedSet(settingAlerts.map((a) => a.id)); };

  return (
    <div className="sticky top-0 z-30" data-testid="alert-banner">
      {/* header with unread count + mute + dismiss-all */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-2" style={{ background: 'rgba(239,68,68,0.22)', borderBottom: '1px solid rgba(239,68,68,0.5)' }}>
        <span className="flex items-center justify-center rounded-full text-xs font-bold shrink-0"
          style={{ background: 'var(--kmt-red)', color: '#fff', minWidth: 22, height: 22, padding: '0 6px' }} data-testid="alert-count">
          {totalCount}
        </span>
        <span className="text-sm font-semibold" style={{ color: 'var(--kmt-red)' }}>
          {totalCount} alert{totalCount === 1 ? '' : 's'} — action needed
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={toggleMute} title={muted ? 'Unmute alerts' : 'Mute alerts'} data-testid="alert-mute-toggle"
            className="w-7 h-7 rounded-md flex items-center justify-center" style={{ color: 'var(--kmt-muted)', background: 'rgba(0,0,0,0.25)' }}>
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>
          <button onClick={dismissAll} data-testid="alert-dismiss-all" className="text-xs px-2 py-1 rounded-md" style={{ color: 'var(--kmt-muted)' }}>Dismiss all</button>
        </div>
      </div>
      {alerts.map((al) => (
        <div key={al.id} className="flex items-center gap-3 px-4 sm:px-6 py-2 kmt-alert-pulse"
          style={{ borderBottom: '1px solid rgba(239,68,68,0.3)' }}>
          <AlertTriangle size={16} style={{ color: 'var(--kmt-red)' }} className="shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <span style={{ color: 'var(--kmt-red)', fontWeight: 700 }} className="font-mono-k">{al.id}</span>
            <span className="ml-2" style={{ color: 'var(--kmt-text)' }}>{al.name}</span>
            <span className="ml-2" style={{ color: 'var(--kmt-muted)' }}>reported a problem — machine is down.</span>
          </div>
          <button onClick={() => navigate('/problems')} data-testid={`alert-view-${al.id}`}
            className="text-xs px-3 py-1 rounded-md shrink-0" style={{ background: 'var(--kmt-red)', color: '#fff', fontWeight: 600 }}>View</button>
          <button onClick={() => dismiss(al.id)} aria-label="Dismiss" data-testid={`alert-dismiss-${al.id}`}
            className="shrink-0" style={{ color: 'var(--kmt-muted)' }}><X size={16} /></button>
        </div>
      ))}
      {visibleLow.map((m) => {
        const isCritical = m.status === 'CRITICAL';
        const color = isCritical ? 'var(--kmt-red)' : 'var(--kmt-amber)';
        return (
          <div key={m.code} className="flex items-center gap-3 px-4 sm:px-6 py-2"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: isCritical ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)' }}
            data-testid={`low-stock-alert-${m.code}`}>
            <PackageX size={16} style={{ color }} className="shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <span style={{ color, fontWeight: 700 }} className="font-mono-k">{m.code}</span>
              <span className="ml-2" style={{ color: 'var(--kmt-text)' }}>{m.name}</span>
              <span className="ml-2" style={{ color: 'var(--kmt-muted)' }}>
                {isCritical ? 'CRITICAL' : 'LOW'} — {m.stock} {m.unit} left (reorder at {m.reorder}).
              </span>
            </div>
            <button onClick={() => navigate('/materials')} data-testid={`low-stock-view-${m.code}`}
              className="text-xs px-3 py-1 rounded-md shrink-0" style={{ background: color, color: '#111', fontWeight: 600 }}>Restock</button>
            <button onClick={() => dismissMat(m.code)} aria-label="Dismiss" data-testid={`low-stock-dismiss-${m.code}`}
              className="shrink-0" style={{ color: 'var(--kmt-muted)' }}><X size={16} /></button>
          </div>
        );
      })}
      {visibleSet.map((a) => (
        <div key={a.id} className="flex items-center gap-3 px-4 sm:px-6 py-2"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(168,85,247,0.10)' }}
          data-testid={`setting-alert-${a.machine}`}>
          <Wrench size={16} style={{ color: '#c084fc' }} className="shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <span style={{ color: '#c084fc', fontWeight: 700 }} className="font-mono-k">{a.machine}</span>
            <span className="ml-2" style={{ color: 'var(--kmt-text)' }}>{a.name}</span>
            <span className="ml-2" style={{ color: 'var(--kmt-muted)' }}>
              material setting running <b style={{ color: '#c084fc' }}>{a.elapsed}m</b> (over {a.threshold}m){a.operator ? ` · ${a.operator}` : ''}{a.job ? ` · ${a.job}` : ''}.
            </span>
          </div>
          <button onClick={() => navigate('/reports')} data-testid={`setting-alert-view-${a.machine}`}
            className="text-xs px-3 py-1 rounded-md shrink-0" style={{ background: '#a855f7', color: '#fff', fontWeight: 600 }}>View</button>
          <button onClick={() => dismissSet(a.id)} aria-label="Dismiss" data-testid={`setting-alert-dismiss-${a.machine}`}
            className="shrink-0" style={{ color: 'var(--kmt-muted)' }}><X size={16} /></button>
        </div>
      ))}
    </div>
  );
}
